import {
    Alert,
    AlertColor,
    Autocomplete,
    Box,
    Button,
    Divider,
    FormControlLabel,
    InputAdornment,
    Link as MuiLink,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import VerticalsComponent from './VerticalsComponent';
import { AddOutlined, InsertLinkOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Dealer } from '../../types/Dealer';
import { ChangeEvent, FC, Fragment, useCallback, useContext } from 'react';
import { UserContext } from '../../App';
import AgencySelector from '../../components/AgencySelector';
import GeographyStateField from '../../components/GeographyStateField';
import BaseWebsiteProviders from '../../data/WebsiteProviders.js';
import DealerGroupAutocomplete from '../../components/Form/Fields/DealerGroupAutocomplete';
import BrandAutocomplete from '../../components/Form/Fields/BrandAutocomplete';
import OemAutocomplete from '../../components/Form/Fields/OemAutocomplete';
import { Agency } from '../../types/Agency';
import OemProgramAutocomplete from '../../components/Form/Fields/OemProgramAutocomplete';
import { useUserContext } from '../../hooks/Contexts/UserContext';
import { useQuery } from '@tanstack/react-query';
import ApiService from '../../ApiService';
import { DmaMapping } from '../../types/DspEntity';

const WebsiteProviders = [
    ...BaseWebsiteProviders.map((websiteProvider) => ({
        name: websiteProvider.name,
        value: websiteProvider.value,
    })),
    { name: 'Sokal', value: 'sokal' },
    { name: 'Jazel', value: 'jazel' },
    { name: 'eDealer', value: 'e_dealer' },
    { name: 'Flex', value: 'flex' },
    { name: 'DealerFire', value: 'dealer_fire' },
    { name: 'Overfuel', value: 'overfuel' },
    { name: 'Other', value: 'other' },
].sort((a, b) => a.name.localeCompare(b.name));

interface DealerOemProgramFieldsetProps {
    dealer: Dealer;
    handleOnDealerUpdate: DealerUpdateCallback;
}

const DealerOemProgramFieldset: FC<DealerOemProgramFieldsetProps> = ({ dealer, handleOnDealerUpdate }) => {
    const { userContext } = useUserContext();

    const oems = dealer?.oems?.map((v: any) => v.oemId) || [];
    const brands = dealer?.brands?.map((v: any) => v.brandId) || [];
    const oemPrograms = dealer?.oemPrograms?.map((v: any) => v.id) || [];

    const isOemsFieldDisabled = !userContext.isAdmin();
    const isBrandsFieldDisabled = !userContext.isAdmin() || oems.length === 0;
    const isOemProgramsFieldDisabled = !userContext.isAdmin() || isBrandsFieldDisabled || brands.length === 0;

    return (
        <Grid container spacing={3}>
            <Grid size={12}>
                <OemAutocomplete
                    label="Select OEMs"
                    multiple
                    disabled={isOemsFieldDisabled}
                    value={oems}
                    criteria={{
                        agencyIds: dealer?.agencyId ? [dealer.agencyId] : undefined,
                    }}
                    onChange={(value: any) => {
                        handleOnDealerUpdate('oems', {
                            oems: value.map((v: any) => ({ oemId: v.id, dealerId: dealer.id })),
                        });
                    }}
                />
            </Grid>
            <Grid size={12}>
                <Tooltip
                    arrow
                    disableHoverListener={!isBrandsFieldDisabled}
                    title="Please fill out OEMs in order to edit this setting."
                >
                    <Box>
                        <BrandAutocomplete
                            label="Select Brands"
                            multiple
                            disabled={isBrandsFieldDisabled}
                            value={brands}
                            criteria={{
                                agencyId: dealer?.agencyId ?? undefined,
                                oemIds: dealer?.oems?.map((v: any) => v.oemId) || undefined,
                            }}
                            onChange={(value: any) => {
                                handleOnDealerUpdate('brands', {
                                    brands: value.map((v: any) => ({ brandId: v.id, dealerId: dealer.id })),
                                });
                            }}
                        />
                    </Box>
                </Tooltip>
            </Grid>
            <Grid size={12}>
                <Tooltip
                    arrow
                    disableHoverListener={!isOemProgramsFieldDisabled}
                    title="Please fill out OEMs and Brands in order to edit this setting."
                >
                    <Box>
                        <OemProgramAutocomplete
                            label="Select OEM Programs"
                            multiple
                            disabled={isOemProgramsFieldDisabled}
                            value={oemPrograms}
                            criteria={{
                                agencyIds: dealer?.agencyId ? [dealer.agencyId] : undefined,
                                oemIds: dealer?.oems?.map((v: any) => v.oemId) || undefined,
                                brandIds: dealer?.brands?.map((v: any) => v.brandId) || undefined,
                            }}
                            onChange={(value: any) => {
                                handleOnDealerUpdate('oemPrograms', {
                                    oemPrograms: value.map((v: any) => ({ id: v.id, dealerId: dealer.id })),
                                });
                            }}
                        />
                    </Box>
                </Tooltip>
            </Grid>
        </Grid>
    );
};

export type DealerUpdateCallback = (name: string, changes: Partial<Dealer>) => void;

export type DealerDetailsEditProps = {
    dealer: Dealer;
    hasDefaultSasAccount: boolean;
    hasDefaultDspEntity: boolean;
    duplicateId?: number;
    // Indicates a partial update of a dealer. The changes object
    // contains only the attributes that changed.
    onDealerUpdate: DealerUpdateCallback;
    className?: string;
    variant?: 'filled' | 'outlined';
    showImagePreviews?: boolean;
    shrinkTextFieldLabels?: boolean;
    onMessage?: (alertLevel: AlertColor, message: string) => void;
    onAgencyDetails: (agency?: Agency) => void;
};

export default function DealerDetailsEdit({
    className,
    dealer,
    duplicateId,
    hasDefaultDspEntity,
    hasDefaultSasAccount,
    onDealerUpdate,
    onMessage,
    variant = 'outlined',
    showImagePreviews = true,
    shrinkTextFieldLabels,
    onAgencyDetails,
}: DealerDetailsEditProps) {
    const { userContext } = useContext(UserContext);

    const handleFieldChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const {
                target: { name, value },
            } = event;

            if (name === 'certifiedStreamingProgram') {
                onDealerUpdate(name, { [name]: event.target.checked });
            } else {
                onDealerUpdate(name, { [name]: value });
            }
        },
        [onDealerUpdate]
    );

    const urlFieldGridSpan = showImagePreviews ? 9 : 12;

    const isOemCodeFieldDisabled = !userContext.isAdmin();
    const isCertifiedStreamingProgramFieldDisabled = !userContext.isAdmin();

    // ============================================================================
    // #region - DMA FIELD LOGIC
    // ============================================================================

    // fetch string list of DMAs [cached in back]
    const { data: activeDmaOptions = [] } = useQuery({
        queryKey: ['dmaDescriptions'],
        queryFn: async () => (await ApiService.getDmaDescriptions()).data,
        staleTime: Infinity,
    });

    // disables field when not a 5-digit number (e.g., invalid zips or out-of-country)
    const isDmaFieldDisabled = !/^\d{5}$/.test(dealer.zip?.trim() ?? '');

    // get selected dmas by id + ensure they're valid
    const selectedDmaIds = dealer.dmaMappings?.map((dm: any) => dm.id) || [];
    const selectedDmas = activeDmaOptions.filter((opt: any) => selectedDmaIds.includes(opt.id));

    // react to zip update
    const handleZipBlur = useCallback(() => {
        const zip = dealer.zip?.trim() || '';

        if (!isDmaFieldDisabled) {
            // fetch zip code mapping from api
            ApiService.getDmaMappingsByZip(zip)
                .then(({ data }) => {
                    // assign all matched zips to dealer [rare, but zips can have multiple DMAs]
                    const matched = data
                        .map((zipDma: any) =>
                            activeDmaOptions.find((opt: DmaMapping) => opt.dmaDescription === zipDma.dmaDescription)
                        )
                        .filter(Boolean);

                    // assign zip dmas || empty
                    onDealerUpdate('dmaMappings', {
                        dmaMappings: matched.length > 0 ? matched.map((dm: any) => ({ id: dm.id })) : [],
                    });
                })

                // handle failed api call
                .catch((err) => console.error('DMA lookup failed: ', err));
        } else {
            onDealerUpdate('dmaMappings', { dmaMappings: [] });
        }
    }, [dealer.zip, isDmaFieldDisabled, onDealerUpdate, activeDmaOptions]);

    // #endregion =====================================================================================

    // UI
    return (
        <Grid container spacing={3} className={className}>
            <Grid size={12}>
                <AgencySelector
                    label="Agency"
                    required={true}
                    agencyId={dealer.agencyId}
                    agencySelected={(agencyId, agencyDetails) => {
                        onAgencyDetails(agencyDetails);
                        onDealerUpdate('agencyId', {
                            agencyId: agencyId,
                        });
                    }}
                    disabled={userContext.isAgency()}
                />
            </Grid>

            <Grid size={12}>
                <TextField
                    name="dealerName"
                    label="Advertiser Name"
                    value={dealer.dealerName ?? ''}
                    required={true}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    name="address"
                    label="Address"
                    value={dealer.address ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={5}>
                <TextField
                    name="city"
                    label="City"
                    value={dealer.city ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={3}>
                <GeographyStateField
                    countryCode={dealer.countryCode}
                    name="state"
                    label="State"
                    value={dealer.state ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={(event: any, value: any) => {
                        onDealerUpdate('state', { state: value?.code ?? '' });
                    }}
                    InputLabelProps={{ shrink: shrinkTextFieldLabels }}
                />
            </Grid>
            <Grid size={2}>
                <TextField
                    name="zip"
                    label="Zip Code"
                    value={dealer.zip ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    onBlur={handleZipBlur}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={2}>
                <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    label="Country"
                    name="countryCode"
                    select
                    value={dealer.countryCode}
                    onChange={handleFieldChange}
                    slotProps={{
                        select: { native: false },
                    }}
                >
                    <MenuItem key={'CA'} value={'CA'}>
                        Canada
                    </MenuItem>
                    <MenuItem key={'US'} value={'US'}>
                        US
                    </MenuItem>
                </TextField>
            </Grid>
            <Grid size={6}>
                <TextField
                    name="phone"
                    label="Phone"
                    value={dealer.phone ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={6} />
            <Grid size={4}>
                <TextField
                    name="agencyAdvertiserId"
                    label="Agency Advertiser Id"
                    value={dealer.agencyAdvertiserId ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    inputProps={{ maxLength: 200 }}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            <Grid size={4}>
                <TextField
                    name="agencyMarketRegion"
                    label="Agency Region/Market"
                    value={dealer.agencyMarketRegion ?? ''}
                    fullWidth={true}
                    size="small"
                    variant={variant}
                    onChange={handleFieldChange}
                    inputProps={{ maxLength: 100 }}
                    slotProps={{
                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>

            {/* DMA ASSIGNMENT FIELD */}
            <Grid size={4}>
                <Tooltip
                    arrow
                    title={isDmaFieldDisabled ? "The advertiser's ZIP must be provided in order to select a DMA." : ''}
                >
                    <Autocomplete
                        size="small"
                        multiple
                        disabled={isDmaFieldDisabled}
                        value={selectedDmas}
                        onChange={(_, newValue: any[]) => {
                            onDealerUpdate('dmaMappings', {
                                dmaMappings: newValue.map((v: any) => ({ id: v.id })),
                            });
                        }}
                        options={activeDmaOptions}
                        getOptionLabel={(option: any) => option.dmaDescription || ''}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="DMA"
                                variant={variant}
                                slotProps={{ inputLabel: { shrink: shrinkTextFieldLabels } }}
                            />
                        )}
                    />
                </Tooltip>
            </Grid>
            <Grid size={12}>
                <Divider />
            </Grid>
            <Grid size={urlFieldGridSpan}>
                <VerticalsComponent
                    verticalId={dealer.verticalId}
                    required={true}
                    onSelectVertical={(verticalId: number) => {
                        onDealerUpdate('verticalId', { verticalId });
                    }}
                    variant={variant}
                    shrinkLabel={shrinkTextFieldLabels}
                />
            </Grid>
            <Grid size={urlFieldGridSpan}>
                <TextField
                    name="url"
                    fullWidth={true}
                    size="small"
                    label="Website URL"
                    required={true}
                    onChange={handleFieldChange}
                    variant={variant}
                    value={dealer.url ?? ''}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="start">
                                    {dealer.url && dealer.url.toLowerCase().indexOf('https://') > -1 && (
                                        <MuiLink href={dealer.url} target="new">
                                            <InsertLinkOutlined />
                                        </MuiLink>
                                    )}
                                </InputAdornment>
                            ),
                        },

                        inputLabel: { shrink: shrinkTextFieldLabels },
                    }}
                />
            </Grid>
            {dealer.verticalId === 2 && (
                <Grid size={urlFieldGridSpan}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        label="Website Provider"
                        name="websiteProvider"
                        select
                        value={dealer?.websiteProvider ?? ''}
                        onChange={handleFieldChange}
                        slotProps={{
                            select: { native: false },
                        }}
                    >
                        <MenuItem value="">- Select website provider -</MenuItem>
                        {WebsiteProviders.map((provider) => {
                            return (
                                <MenuItem key={provider.value} value={provider.value}>
                                    {provider.name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Grid>
            )}
            <Grid size={urlFieldGridSpan}>
                <DealerGroupAutocomplete
                    multiple={false}
                    agencyId={dealer.agencyId ?? 0}
                    value={dealer?.dealerGroupId ?? null}
                    onChange={(value: any) => {
                        onDealerUpdate('dealerGroupId', { dealerGroupId: value?.id || null });
                    }}
                />
            </Grid>
            {!userContext.isOpsAdmin() && (
                <Grid size={12}>
                    <Divider />
                </Grid>
            )}
            {!userContext.isOpsAdmin() && (
                <Grid size={12}>
                    <h4>OEM Program Settings</h4>
                </Grid>
            )}
            {!userContext.isOpsAdmin() && dealer.verticalId === 2 && (
                <Fragment>
                    <Grid size={urlFieldGridSpan}>
                        <TextField
                            name="oemCode"
                            fullWidth={true}
                            disabled={isOemCodeFieldDisabled}
                            size="small"
                            label="OEM - Advertiser Code"
                            required={false}
                            onChange={handleFieldChange}
                            variant={variant}
                            value={dealer.oemCode ?? ''}
                        />
                    </Grid>

                    <Grid size={urlFieldGridSpan}>
                        <DealerOemProgramFieldset dealer={dealer} handleOnDealerUpdate={onDealerUpdate} />
                    </Grid>

                    <Grid size={urlFieldGridSpan}>
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={isCertifiedStreamingProgramFieldDisabled}
                                    checked={dealer.certifiedStreamingProgram}
                                    onChange={handleFieldChange}
                                    name="certifiedStreamingProgram"
                                />
                            }
                            label="Certified Streaming Program"
                        />
                    </Grid>
                </Fragment>
            )}
            {userContext.isAgency() && duplicateId != null && (
                <>
                    <Grid size={10}>
                        <Alert sx={{ marginBottom: '10px' }} severity="error">
                            You already have an Advertiser with the same name. Would you like to launch a new Campaign
                            instead ?
                        </Alert>

                        <Link to="/campaign/v2/create" state={{ dealerId: duplicateId }}>
                            <Button variant="contained" color="primary" startIcon={<AddOutlined />}>
                                New Campaign
                            </Button>
                        </Link>
                    </Grid>
                </>
            )}
        </Grid>
    );
}
